import { Link } from "@tanstack/react-router";
import { Fragment, useCallback, useEffect, useState, type ReactNode } from "react";
import { ROLE_LABEL, Shell } from "@/app/Shell";
import { TabBar } from "@/app/TabBar";
import { useDashboard, useRoute, useStep, useTick, type Route, type RouteStep, type RouteTrack, type StepView } from "@/app/queries";
import { useMe } from "@/app/session";
import { count, plural } from "@/lib/plural";
import { CheckRow } from "@/ui/CheckRow";
import { EmptyState, NoAccess, SkeletonRows, StateCard } from "@/ui/states";
import { errorInfo } from "./step/model";

/* «Дополнительная практика» (/practice): доп. задачи открытых шагов по
   трекам и «Если ждёшь ментора» — вопросы «Проверь себя», ещё не
   отмеченные «знаю». Всё это вне гейта: отметки extra и quiz не входят
   ни в критерии, ни в «N / M задач» шага. Нового API нет — маршрут и
   документы открытых шагов. */

export type Task = StepView["content"]["practice"]["extra"][number];
export type QA = NonNullable<StepView["content"]["blocks"][string]["items"]>[number];
export type OpenStep = { step: RouteStep; track: RouteTrack };

/** Все незакрытые шаги (сданные, в работе, доступные) в порядке маршрута. */
export function openSteps(route: Route): OpenStep[] {
  return route.tracks.flatMap((track) => track.steps.filter((s) => s.state !== "locked").map((step) => ({ step, track })));
}

export function extraTasks(v: StepView): Task[] {
  return v.content.practice.extra ?? [];
}

export function extraDone(v: StepView): number {
  const set = new Set(v.ticks.extra ?? []);
  return extraTasks(v).filter((t) => set.has(t.id)).length;
}

/** Вопросы «Проверь себя» включённого блока. */
export function quizItems(v: StepView): QA[] {
  const b = v.content.blocks.quiz;
  return b && b.on ? (b.items ?? []) : [];
}

/** Вопросы, ещё не отмеченные «знаю». */
export function unknownQuiz(v: StepView): QA[] {
  const known = new Set(v.ticks.quiz ?? []);
  return quizItems(v).filter((q) => !known.has(q.id));
}

/** «3 задачи в 2 шагах · сделано 1» — из документов шагов. */
export function extraSummary(views: StepView[]): { tasks: number; steps: number; done: number; label: string } {
  const withExtra = views.filter((v) => extraTasks(v).length > 0);
  const tasks = withExtra.reduce((n, v) => n + extraTasks(v).length, 0);
  const done = withExtra.reduce((n, v) => n + extraDone(v), 0);
  const steps = withExtra.length;
  const label = tasks === 0 ? count(0, "задача", "задачи", "задач") : `${count(tasks, "задача", "задачи", "задач")} в ${steps} ${plural(steps, "шаге", "шагах", "шагах")} · сделано ${done}`;
  return { tasks, steps, done, label };
}

/** Текст с `кодом` в обратных кавычках → <code>. */
export function withCode(text: string): ReactNode {
  return text.split("`").map((part, i) =>
    i % 2 === 1 ? (
      <code key={i} className="rounded-[3px] bg-bg2 px-1 font-mono text-[0.92em] text-ink">
        {part}
      </code>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    ),
  );
}

type Load = { status: "pending" } | { status: "success"; view: StepView } | { status: "error"; skip: boolean; retry: () => void };

function sameLoad(a: Load | undefined, b: Load): boolean {
  if (!a || a.status !== b.status) return false;
  if (a.status === "success" && b.status === "success") return a.view === b.view;
  if (a.status === "error" && b.status === "error") return a.skip === b.skip;
  return true;
}

/** Загружает документ шага и сообщает состояние наверх — хуки нельзя звать в цикле. */
function StepProbe({ id, onLoad }: { id: string; onLoad: (id: string, load: Load) => void }) {
  const q = useStep(id);
  const { status, data, error, refetch } = q;
  useEffect(() => {
    if (status === "success" && data) onLoad(id, { status: "success", view: data });
    else if (status === "error") {
      const e = errorInfo(error);
      // Закрытый или удалённый шаг (маршрут устарел) — просто не показываем.
      onLoad(id, { status: "error", skip: e.status === 403 || e.status === 404, retry: () => void refetch() });
    } else onLoad(id, { status: "pending" });
  }, [id, status, data, error, refetch, onLoad]);
  return null;
}

type TickVars = { kind: "extra" | "quiz"; key: string; on: boolean };

/** Отметка с откатом: при ошибке — причина и «повторить» рядом. */
function useSafeTick(stepId: string) {
  const mut = useTick(stepId);
  const [failed, setFailed] = useState<TickVars | null>(null);
  const tick = (vars: TickVars) => {
    setFailed(null);
    mut.mutate(vars, { onError: () => setFailed(vars) });
  };
  return { tick, failed };
}

function TickError({ failed, retry }: { failed: TickVars | null; retry: (v: TickVars) => void }) {
  if (!failed) return null;
  return (
    <div role="alert" className="flex items-center gap-3 border-t border-bd bg-errsoft px-3.5 py-2.5">
      <span className="min-w-0 flex-1 text-[12.5px] leading-[1.45] text-ink2">Отметка не сохранилась и вернулась в прежнее состояние. Проверь связь и попробуй ещё раз.</span>
      <button type="button" onClick={() => retry(failed)} className="flex-none cursor-pointer rounded-sm border border-err bg-transparent px-3 py-1.5 font-mono text-[11.5px] text-err">
        повторить
      </button>
    </div>
  );
}

const STATE_LABEL: Record<StepView["state"], string> = { done: "гейт сдан", active: "в работе", open: "доступен", locked: "закрыт" };

function stepMeta(v: StepView): string {
  const state = v.gate_status === "submitted" ? "гейт ждёт ментора" : STATE_LABEL[v.state];
  return `шаг ${v.step_index} из ${v.track_total} · ${state}`;
}

/** Карточка доп. задач одного шага. */
function ExtraCard({ view }: { view: StepView }) {
  const { tick, failed } = useSafeTick(view.id);
  const tasks = extraTasks(view);
  const done = extraDone(view);
  const set = new Set(view.ticks.extra ?? []);
  return (
    <section className="overflow-hidden rounded-md border border-bd bg-card" data-testid={`extra-${view.id}`} aria-labelledby={`extra-title-${view.id}`}>
      <div className="flex items-start gap-3 border-b border-bd px-3.5 py-[11px]">
        <div className="min-w-0 flex-1">
          <h3 id={`extra-title-${view.id}`} className="m-0 text-[13.5px] font-semibold leading-[1.35] text-ink">
            {view.title}
          </h3>
          <div className="mt-0.5 font-mono text-[11px] text-mut">{stepMeta(view)}</div>
        </div>
        <span className="flex-none rounded-sm border border-dashed border-bd2 px-1.5 py-0.5 font-mono text-[11px] text-ink2">вне гейта</span>
        <span className={`flex-none font-mono text-[11px] leading-[22px] ${done === tasks.length ? "text-ok" : "text-mut"}`}>
          {done} / {tasks.length}
        </span>
      </div>
      {tasks.map((t) => (
        <div key={t.id} className="border-b border-bd px-3.5 last:border-b-0">
          <CheckRow checked={set.has(t.id)} onChange={(on) => tick({ kind: "extra", key: t.id, on })} label={<span className="text-pretty">{withCode(t.text)}</span>} />
        </div>
      ))}
      <TickError failed={failed} retry={tick} />
      <div className="border-t border-bd px-3.5 py-2">
        <Link to="/step/$id" params={{ id: view.id }} className="font-mono text-[11.5px] text-acc no-underline hover:underline">
          открыть шаг
        </Link>
      </div>
    </section>
  );
}

function QuizItem({ item, onKnow }: { item: QA; onKnow: () => void }) {
  const [open, setOpen] = useState(false);
  const hasAnswer = item.a.trim() !== "";
  return (
    <li className="border-b border-bd px-3.5 py-2.5 last:border-b-0">
      <div className="text-[13px] leading-[1.5] text-ink text-pretty">{withCode(item.q)}</div>
      {open && hasAnswer && <div className="mt-2 rounded-sm border border-bd bg-bg2 px-2.5 py-2 text-[12.5px] leading-[1.5] text-ink2 text-pretty [overflow-wrap:anywhere]">{withCode(item.a)}</div>}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {hasAnswer ? (
          <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)} className="cursor-pointer rounded-sm border border-bd2 bg-transparent px-2.5 py-1 font-mono text-[11px] text-ink2 hover:text-ink">
            {open ? "скрыть ответ" : "показать ответ"}
          </button>
        ) : (
          <span className="font-mono text-[11px] text-mut">ответа пока нет — спроси ментора</span>
        )}
        <button type="button" onClick={onKnow} className="cursor-pointer rounded-sm border border-acc bg-transparent px-2.5 py-1 font-mono text-[11px] text-acc hover:bg-accsoft">
          знаю
        </button>
      </div>
    </li>
  );
}

function QuizGroup({ view, items }: { view: StepView; items: QA[] }) {
  const { tick, failed } = useSafeTick(view.id);
  return (
    <div className="overflow-hidden rounded-md border border-bd bg-card" data-testid={`quiz-${view.id}`}>
      <div className="border-b border-bd px-3.5 py-[9px]">
        <div className="text-[12.5px] font-semibold text-ink">{view.title}</div>
        <div className="font-mono text-[11px] text-mut">{view.track}</div>
      </div>
      <ul className="m-0 list-none p-0">
        {items.map((q) => (
          <QuizItem key={q.id} item={q} onKnow={() => tick({ kind: "quiz", key: q.id, on: true })} />
        ))}
      </ul>
      <TickError failed={failed} retry={tick} />
    </div>
  );
}

/** «Если ждёшь ментора»: вопросы «Проверь себя» открытых шагов без «знаю». */
function WaitingQuiz({ views }: { views: StepView[] }) {
  const groups = views.map((v) => ({ view: v, items: unknownQuiz(v) })).filter((g) => g.items.length > 0);
  const total = groups.reduce((n, g) => n + g.items.length, 0);
  const anyQuiz = views.some((v) => quizItems(v).length > 0);
  const waiting = views.filter((v) => v.gate_status === "submitted");
  return (
    <aside className="flex flex-col gap-3" aria-labelledby="waiting-title" data-testid="waiting-quiz">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="waiting-title" className="m-0 font-mono text-[13px] font-semibold uppercase tracking-[.04em] text-mut">
          если ждёшь ментора
        </h2>
        {total > 0 && (
          <span className="font-mono text-[11px] text-mut" data-testid="quiz-count">
            {count(total, "вопрос", "вопроса", "вопросов")} без отметки «знаю»
          </span>
        )}
      </div>
      {waiting.map((v) => (
        <p key={v.id} className="m-0 rounded-sm border border-bd bg-warnsoft px-3 py-2 text-[12.5px] leading-[1.45] text-ink2">
          Гейт шага «{v.title}» ждёт ментора.
        </p>
      ))}
      <p className="m-0 text-[12.5px] leading-[1.5] text-ink2 text-pretty">Вопросы «Проверь себя» из открытых шагов, которые ты ещё не отметил «знаю». Это самопроверка — в гейт не входит.</p>
      {groups.map((g) => (
        <QuizGroup key={g.view.id} view={g.view} items={g.items} />
      ))}
      {total === 0 && (
        <p className="m-0 rounded-md border border-bd bg-card px-3.5 py-3 text-[12.5px] leading-[1.5] text-ink2">
          {anyQuiz ? "Все вопросы «Проверь себя» отмечены «знаю»." : "В открытых шагах пока нет вопросов «Проверь себя»."}
        </p>
      )}
    </aside>
  );
}

export function PracticePage() {
  const me = useMe();
  const route = useRoute();
  const dash = useDashboard();
  const [loads, setLoads] = useState<Record<string, Load>>({});
  const onLoad = useCallback((id: string, load: Load) => {
    setLoads((cur) => (sameLoad(cur[id], load) ? cur : { ...cur, [id]: load }));
  }, []);

  const user = me.data ? { name: me.data.name, role: ROLE_LABEL[me.data.role] ?? me.data.role } : undefined;
  const metaParts = [dash.data?.cohort && `когорта ${dash.data.cohort}`, dash.data?.mentor && `ментор ${dash.data.mentor}`].filter(Boolean);
  const meta = metaParts.length > 0 ? metaParts.join(" · ") : undefined;

  if (route.status === "pending") {
    return (
      <Shell nav user={user} meta={meta}>
        <SkeletonRows rows={6} />
        <TabBar />
      </Shell>
    );
  }
  if (route.status === "error") {
    const e = errorInfo(route.error);
    return (
      <Shell nav user={user} meta={meta}>
        {e.status === 403 ? (
          <NoAccess where="студент · доп. практика" body="Дополнительная практика — часть маршрута студента. Материалы шагов открыты в конструкторе курса." primary={{ label: "на главную", href: "/" }} />
        ) : (
          <StateCard
            code="ERR_NETWORK"
            where="студент · доп. практика"
            tone="warn"
            title="Нет связи с сервером"
            body="Маршрут не загрузился. Отметки доп. задач не потеряны — они на сервере; повтори запрос, когда связь вернётся."
            primary={{ label: "повторить сейчас", onClick: () => void route.refetch() }}
          />
        )}
        <TabBar />
      </Shell>
    );
  }

  const open = openSteps(route.data);
  const loadOf = (id: string): Load => loads[id] ?? { status: "pending" };
  const views = open.map((s) => loadOf(s.step.id)).flatMap((l) => (l.status === "success" ? [l.view] : []));
  const pending = open.filter((s) => loadOf(s.step.id).status === "pending").length;
  const failed = open.map((s) => loadOf(s.step.id)).filter((l): l is Extract<Load, { status: "error" }> => l.status === "error" && !l.skip);
  const summary = extraSummary(views);
  const byId = new Map(views.map((v) => [v.id, v]));
  const groups = route.data.tracks
    .map((track) => ({ track, views: track.steps.map((s) => byId.get(s.id)).filter((v): v is StepView => v !== undefined && extraTasks(v).length > 0) }))
    .filter((g) => g.views.length > 0);

  return (
    <Shell nav user={user} meta={meta}>
      {open.map((s) => (
        <StepProbe key={s.step.id} id={s.step.id} onLoad={onLoad} />
      ))}
      <header>
        <div className="mb-3 font-mono text-[11px] uppercase tracking-[.08em] text-accdim">студент · вне гейта</div>
        <h1 className="m-0 font-mono text-[28px] font-bold leading-[1.1] tracking-[-.02em] text-ink md:text-[34px]">Дополнительная практика</h1>
        <p className="mb-0 mt-3 max-w-[680px] text-[13.5px] leading-[1.55] text-ink2 text-pretty">
          Задачи сверх базового уровня и применения из открытых шагов. В гейт они не входят и прогресс шага не двигают — это тренировка: закрепить шаг или занять время, пока работа у ментора.
        </p>
        <div className="mt-2.5 font-mono text-[11.5px] text-mut" data-testid="practice-summary">
          {summary.label}
        </div>
      </header>
      <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_336px] md:gap-8">
        <div className="flex min-w-0 flex-col gap-6">
          {failed.length > 0 && (
            <div role="status" className="flex items-center gap-3 rounded-md border border-warn bg-warnsoft px-3.5 py-2.5" data-testid="practice-errors">
              <span className="min-w-0 flex-1 text-[12.5px] leading-[1.45] text-ink2">
                Не {plural(failed.length, "загрузился", "загрузились", "загрузились")} {count(failed.length, "шаг", "шага", "шагов")} — нет связи с сервером. Отметки не потеряны, задачи этих шагов появятся после повтора.
              </span>
              <button type="button" onClick={() => failed.forEach((f) => f.retry())} className="flex-none cursor-pointer rounded-sm border border-warn bg-transparent px-3 py-1.5 font-mono text-[11.5px] text-warn">
                повторить
              </button>
            </div>
          )}
          {groups.map((g) => (
            <section key={g.track.id} className="flex flex-col gap-3" data-testid="practice-track" aria-labelledby={`track-${g.track.slug}`}>
              <h2 id={`track-${g.track.slug}`} className="m-0 font-mono text-[13px] font-semibold uppercase tracking-[.04em] text-mut">
                {g.track.name}
                {g.track.parallel && " · параллельно"}
              </h2>
              {g.views.map((v) => (
                <ExtraCard key={v.id} view={v} />
              ))}
            </section>
          ))}
          {pending > 0 && <SkeletonRows rows={Math.min(pending, 3)} />}
          {pending === 0 && summary.tasks === 0 && failed.length === 0 && (
            <EmptyState
              where="студент · доп. практика"
              title="Доп. задач пока нет"
              body="В открытых шагах нет задач сверх гейта. Они появятся, когда менторы добавят их в шаг, — основная практика и гейт от этого не зависят."
              primary={{ label: "на мой маршрут", href: "/" }}
            />
          )}
        </div>
        <WaitingQuiz views={views} />
      </div>
      <TabBar />
    </Shell>
  );
}
